import { useMemo } from 'react';
import { getStoredUserData } from '@core/utils/auth';
import { Module, ModulePermission, Permission } from '@modules/admin/types';

interface PermissionCheck {
  canView: boolean;
  canAdd: boolean;
  canUpdate: boolean;
  hasAnyPermission: boolean;
  permissions: Permission[];
}

/**
 * Custom hook to check user permissions for a specific module
 * @param module - The module to check permissions for (using Module enum)
 * @returns Object with boolean flags for view, add, and update permissions
 *
 * @example
 * const { canView, canAdd, canUpdate } = usePermission(Module.OFFER);
 */
export const usePermission = (module: Module): PermissionCheck => {
  const userData = getStoredUserData();

  const permissionCheck = useMemo((): PermissionCheck => {
    // If no user data, deny all permissions
    if (!userData?.user?.modules) {
      return {
        canView: false,
        canAdd: false,
        canUpdate: false,
        hasAnyPermission: false,
        permissions: [],
      };
    }

    // Find the module in user's permissions
    const modulePermissions: ModulePermission | undefined = userData.user.modules.find(
      (m) => m.module_name.toLowerCase() === module.toLowerCase(),
    );

    // If module not found in user's permissions, deny all
    if (!modulePermissions) {
      return {
        canView: false,
        canAdd: false,
        canUpdate: false,
        hasAnyPermission: false,
        permissions: [],
      };
    }

    // Normalize permissions to lowercase for case-insensitive comparison
    const normalizedPermissions = modulePermissions.permissions.map((p) => p.toLowerCase());

    const canView = normalizedPermissions.includes('view');
    const canAdd = normalizedPermissions.includes('add');
    const canUpdate = normalizedPermissions.includes('update');

    return {
      canView,
      canAdd,
      canUpdate,
      hasAnyPermission: canView || canAdd || canUpdate,
      permissions: modulePermissions.permissions,
    };
  }, [userData, module]);

  return permissionCheck;
};

/**
 * Helper function to check if user has a specific permission for a module
 * Useful for non-hook contexts or when you need to check permissions imperatively
 * @param module - The module to check
 * @param permission - The permission to check ('view', 'add', or 'update')
 * @returns boolean indicating if user has the permission
 */
export const checkPermission = (module: Module, permission: 'view' | 'add' | 'update'): boolean => {
  const userData = getStoredUserData();

  if (!userData?.user?.modules) {
    return false;
  }

  const modulePermissions = userData.user.modules.find((m) => m.module_name.toLowerCase() === module.toLowerCase());

  if (!modulePermissions) {
    return false;
  }

  const normalizedPermissions = modulePermissions.permissions.map((p) => p.toLowerCase());
  return normalizedPermissions.includes(permission.toLowerCase());
};
